import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  getDashboardStats, 
  getTasks, 
  getTodayNotes, 
  getTodayCashSession
} from '../lib/api';
import { formatCurrency, formatDateTime, statusLabels, getStatusClass, getPriorityClass, priorityLabels, getTodayDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { 
  Plus, 
  Wallet, 
  Users, 
  AlertTriangle,
  CheckSquare,
  UserPlus,
  CreditCard,
  Package,
  Clock,
  ArrowRight,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Loader2
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [cashRegister, setCashRegister] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, tasksRes, notesRes, cashRes] = await Promise.all([
        getDashboardStats(),
        getTasks({ status: 'pendiente' }),
        getTodayNotes(),
        getTodayCashSession()
      ]);
      setStats(statsRes.data);
      // Show today's due tasks first, then other pending tasks
      const allTasks = tasksRes.data || [];
      const today = getTodayDate();
      const todayTasks = allTasks.filter(t => t.due_date && t.due_date.startsWith(today));
      const otherTasks = allTasks.filter(t => !t.due_date || !t.due_date.startsWith(today));
      setTasks([...todayTasks, ...otherTasks].slice(0, 7));
      setNotes(notesRes.data);
      setCashRegister(cashRes.data);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2B4257]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-recepcion">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">
          Hola {user?.name}, aquí está el resumen del día
        </p>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions" data-testid="quick-actions">
        <Link to="/casos-operativos">
          <Button className="btn-primary gap-2" data-testid="btn-new-case">
            <Plus size={18} />
            Control Mensual
          </Button>
        </Link>
        <Link to="/caja">
          <Button className="btn-secondary gap-2" data-testid="btn-cash-register">
            <Wallet size={18} />
            {cashRegister ? 'Ver Caja' : 'Abrir Caja'}
          </Button>
        </Link>
        <Link to="/leads?action=new">
          <Button className="btn-secondary gap-2" data-testid="btn-new-lead">
            <UserPlus size={18} />
            Nuevo Lead
          </Button>
        </Link>
      </div>

      {/* Daily Notes from Direction - PROMINENT */}
      {notes.length > 0 && (
        <div className="relative overflow-hidden rounded-xl border-2 border-[#D98A6C] bg-gradient-to-r from-[#2B4257] to-[#3D5A75] text-white p-6 shadow-lg" data-testid="daily-notes-card">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#D98A6C]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#D98A6C]/10 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative flex items-start gap-4">
            <div className="p-3 bg-[#D98A6C] rounded-xl flex-shrink-0">
              <MessageSquare size={24} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <h3 className="font-bold text-lg">Instrucciones del Día</h3>
                <span className="px-2 py-0.5 bg-[#D98A6C] text-white text-xs font-semibold rounded-full animate-pulse">IMPORTANTE</span>
              </div>
              <div className="space-y-2">
                {notes.map((note) => (
                  <div key={note.id} className="flex items-start gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-3">
                    <span className="text-[#D98A6C] mt-0.5 flex-shrink-0">&#9670;</span>
                    <p className="text-white/90 text-sm leading-relaxed">{note.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="dashboard-grid" data-testid="stats-grid">
        {/* Pending Tasks */}
        <div className="card stat-card" data-testid="stat-pending-tasks">
          <div className="flex items-center justify-between">
            <CheckSquare className="text-[#2B4257]" size={24} />
            {stats?.overdue_tasks > 0 && (
              <span className="status-badge status-cancelado text-xs">
                {stats.overdue_tasks} vencidas
              </span>
            )}
          </div>
          <div className="stat-value">{stats?.pending_tasks || 0}</div>
          <div className="stat-label">Tareas Pendientes</div>
        </div>

        {/* New Leads */}
        <div className="card stat-card" data-testid="stat-new-leads">
          <div className="flex items-center justify-between">
            <UserPlus className="text-[#D98A6C]" size={24} />
            {stats?.new_leads > 0 && (
              <span className="status-badge badge-warning text-xs">Nuevos</span>
            )}
          </div>
          <div className="stat-value">{stats?.new_leads || 0}</div>
          <div className="stat-label">Leads por Contactar</div>
        </div>

        {/* Rejected Payments */}
        <div className="card stat-card" data-testid="stat-rejected-payments">
          <div className="flex items-center justify-between">
            <CreditCard className="text-[#C25953]" size={24} />
            {stats?.rejected_payments > 0 && (
              <span className="status-badge status-cancelado text-xs">Urgente</span>
            )}
          </div>
          <div className="stat-value">{stats?.rejected_payments || 0}</div>
          <div className="stat-label">Pagos Rechazados</div>
        </div>

        {/* Low Stock */}
        <div className="card stat-card" data-testid="stat-low-stock">
          <div className="flex items-center justify-between">
            <Package className="text-[#5C7083]" size={24} />
            {stats?.low_stock_products > 0 && (
              <span className="status-badge status-congelado text-xs">Stock bajo</span>
            )}
          </div>
          <div className="stat-value">{stats?.low_stock_products || 0}</div>
          <div className="stat-label">Productos Stock Bajo</div>
        </div>
      </div>

      {/* Alerts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Clients to Recontact */}
        {stats?.clients_to_recontact > 0 && (
          <div className="alert-card alert-warning" data-testid="alert-recontact">
            <AlertTriangle size={20} />
            <div>
              <p className="font-medium text-[#1A2835]">Clientes por recontactar</p>
              <p className="text-sm text-[#5C7083]">
                {stats.clients_to_recontact} clientes cancelaron hace 3 meses
              </p>
            </div>
            <Link to="/clientes?status=cancelado" className="ml-auto">
              <Button variant="ghost" size="sm" className="btn-ghost">
                Ver <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
        )}

        {/* Cash Register Status */}
        <div className={`alert-card ${cashRegister ? 'alert-success' : 'alert-warning'}`} data-testid="alert-cash">
          <Wallet size={20} />
          <div>
            <p className="font-medium text-[#1A2835]">
              {cashRegister ? (cashRegister.estado_caja === 'abierta' ? 'Caja Abierta' : 'Caja Cerrada') : 'Caja Sin Abrir'}
            </p>
            <p className="text-sm text-[#5C7083]">
              {cashRegister 
                ? `Efectivo inicial: ${formatCurrency(cashRegister.efectivo_inicial)}`
                : 'Recuerda abrir la caja para registrar ventas'
              }
            </p>
          </div>
          <Link to="/caja" className="ml-auto">
            <Button variant="ghost" size="sm" className="btn-ghost">
              {cashRegister ? 'Ver' : 'Abrir'} <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>

        {/* Expiring Plans Alert */}
        {stats?.expiring_plans > 0 && (
          <div className="alert-card alert-warning" data-testid="alert-expiring-plans">
            <Clock size={20} />
            <div>
              <p className="font-medium text-[#1A2835]">Planes por vencer</p>
              <p className="text-sm text-[#5C7083]">
                {stats.expiring_plans} {stats.expiring_plans === 1 ? 'cliente tiene su plan' : 'clientes tienen su plan'} por vencer en los próximos 7 días
              </p>
            </div>
            <Link to="/clientes?status=activo" className="ml-auto">
              <Button variant="ghost" size="sm" className="btn-ghost">
                Ver <ArrowRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Tasks */}
        <div className="card" data-testid="tasks-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1A2835]">Tareas Pendientes</h3>
            <Link to="/tareas">
              <Button variant="ghost" size="sm" className="btn-ghost text-sm">
                Ver todas <ArrowRight size={14} className="ml-1" />
              </Button>
            </Link>
          </div>
          
          {tasks.length === 0 ? (
            <div className="empty-state py-8">
              <CheckSquare className="empty-state-icon mx-auto" />
              <p>No hay tareas pendientes</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div 
                  key={task.id} 
                  className="flex items-center gap-3 p-3 bg-[#F5F0E8] rounded-lg"
                  data-testid={`task-item-${task.id}`}
                >
                  <div className={`status-badge ${getPriorityClass(task.priority)}`}>
                    {priorityLabels[task.priority]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#1A2835] truncate">{task.title}</p>
                    {task.due_date && (
                      <p className="text-xs text-[#5C7083] flex items-center gap-1 mt-0.5">
                        <Clock size={12} />
                        {formatDateTime(task.due_date)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Today's Summary */}
        <div className="card" data-testid="summary-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-[#1A2835]">Resumen del Día</h3>
          </div>
          
          <div className="space-y-4">
            {/* Today Sales */}
            <div className="flex items-center justify-between p-3 bg-[#EDF5F0] rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="text-[#4A7C59]" size={20} />
                <span className="text-[#1A2835]">Ventas Hoy</span>
              </div>
              <span className="font-semibold text-[#4A7C59]">{formatCurrency(stats?.today_sales || 0)}</span>
            </div>

            {/* Week Sales */}
            <div className="flex items-center justify-between p-3 bg-[#F5F0E8] rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingUp className="text-[#2B4257]" size={20} />
                <span className="text-[#1A2835]">Ventas Semana</span>
              </div>
              <span className="font-semibold text-[#2B4257]">{formatCurrency(stats?.week_sales || 0)}</span>
            </div>

            {/* New Clients */}
            <div className="flex items-center justify-between p-3 bg-[#F5F0E8] rounded-lg">
              <div className="flex items-center gap-3">
                <Users className="text-[#2B4257]" size={20} />
                <span className="text-[#1A2835]">Nuevos Clientes (mes)</span>
              </div>
              <span className="font-semibold text-[#2B4257]">{stats?.new_clients_month || 0}</span>
            </div>

            {/* Cancellations */}
            <div className="flex items-center justify-between p-3 bg-[#FBEAE9] rounded-lg">
              <div className="flex items-center gap-3">
                <TrendingDown className="text-[#C25953]" size={20} />
                <span className="text-[#1A2835]">Cancelaciones (mes)</span>
              </div>
              <span className="font-semibold text-[#C25953]">{stats?.cancellations_month || 0}</span>
            </div>

            {/* Net Growth */}
            <div className={`flex items-center justify-between p-3 rounded-lg ${stats?.net_growth >= 0 ? 'bg-[#EDF5F0]' : 'bg-[#FBEAE9]'}`}>
              <div className="flex items-center gap-3">
                {stats?.net_growth >= 0 ? (
                  <TrendingUp className="text-[#4A7C59]" size={20} />
                ) : (
                  <TrendingDown className="text-[#C25953]" size={20} />
                )}
                <span className="text-[#1A2835]">Crecimiento Neto</span>
              </div>
              <span className={`font-semibold ${stats?.net_growth >= 0 ? 'text-[#4A7C59]' : 'text-[#C25953]'}`}>
                {stats?.net_growth >= 0 ? '+' : ''}{stats?.net_growth || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
